import AbstractComponent from "./abstract-component";
import {
    RevealBlockObserver,
    RevealObserver,
    animateRevealNodes,
    getRevealConfig,
    prefersReducedMotion,
    prepareRevealNodes,
    revealInstant,
    waitForLoaderDone,
} from "../utils/reveal";

export default class Reveal extends AbstractComponent {
    constructor(el) {
        super(el);
        this._started = false;
        this.DOM = {};
        this._blockObserver = new RevealBlockObserver();
        this._itemObserver = new RevealObserver();
        this._titleObserver = new RevealObserver({ threshold: 0.15, rootMargin: '0px 0px -10% 0px' });
        this.$header = this.$el.closest(".c-header, .c-header-simple") || null;

        this.initDom();
        this.prepareInitialState();
        waitForLoaderDone().then(() => this.start());
    }

    initDom() {
        this.DOM.titlesSt2 = Array.from(this.$el.querySelectorAll(".st2"));
        this.DOM.titlesSt1 = Array.from(this.$el.querySelectorAll("h1"));
        this.DOM.revealBlocks = Array.from(this.$el.querySelectorAll("[data-reveal]"));

        if (this.$header) {
            this.DOM.desc = this.$header.querySelector(".c-header__description, .c-header-simple__description");
            this.DOM.buttons = this.$header.querySelector(".c-header__buttons, .c-header-simple__buttons");
        } else {
            this.DOM.desc = null;
            this.DOM.buttons = null;
        }
    }

    prepareInitialState() {
        if (prefersReducedMotion()) return;

        const config = getRevealConfig();

        if (this.$header) {
            const h1 = this.DOM.titlesSt1[0];
            if (h1) prepareRevealNodes([h1], { ...config, y: 20 });
            if (this.DOM.desc) prepareRevealNodes([this.DOM.desc], { ...config, y: 16 });
            if (this.DOM.buttons) prepareRevealNodes([this.DOM.buttons], { ...config, y: 16 });
        }

        this.DOM.revealBlocks.forEach((block) => {
            const items = Array.from(block.querySelectorAll('[data-reveal-item]'));
            prepareRevealNodes(items, config);
        });

        const orphanItems = Array.from(this.$el.querySelectorAll('[data-reveal-item]')).filter((item) => {
            return !item.closest('[data-reveal]');
        });
        prepareRevealNodes(orphanItems, config);

        this.DOM.titlesSt2.forEach((title) => {
            if (this.$header && this.$header.contains(title)) return;
            prepareRevealNodes([title], config);
        });
    }

    start() {
        if (this._started) return;
        this._started = true;

        if (prefersReducedMotion()) {
            this.showAllInstant();
            return;
        }

        if (this.$header) {
            this.animateHeader();
        }

        this.wireReveals();
        this.prepareSt2();
    }

    showAllInstant() {
        this.DOM.revealBlocks.forEach((block) => {
            revealInstant(Array.from(block.querySelectorAll('[data-reveal-item]')));
        });

        revealInstant(Array.from(this.$el.querySelectorAll('[data-reveal-item]')).filter((item) => !item.closest('[data-reveal]')));
        revealInstant(this.DOM.titlesSt2);
        revealInstant(this.DOM.titlesSt1);

        if (this.DOM.desc) revealInstant([this.DOM.desc]);
        if (this.DOM.buttons) revealInstant([this.DOM.buttons]);
    }

    animateHeader() {
        const config = getRevealConfig();
        const timeline = this.app.gsap.timeline();
        const h1 = this.DOM.titlesSt1[0];

        if (h1) {
            timeline.to(h1, {
                y: 0,
                autoAlpha: 1,
                duration: config.duration,
                ease: config.ease,
            });
        }

        if (this.DOM.desc) {
            timeline.to(this.DOM.desc, {
                y: 0,
                autoAlpha: 1,
                duration: config.duration * 0.9,
                ease: config.ease,
            }, h1 ? '-=0.35' : 0);
        }

        if (this.DOM.buttons) {
            timeline.to(this.DOM.buttons, {
                y: 0,
                autoAlpha: 1,
                duration: config.duration * 0.9,
                ease: config.ease,
            }, '-=0.35');
        }
    }

    wireReveals() {
        this._blockObserver.observe(this.DOM.revealBlocks);

        const orphanItems = Array.from(this.$el.querySelectorAll('[data-reveal-item]')).filter((item) => {
            return !item.closest('[data-reveal]');
        });

        this._itemObserver.observe(orphanItems);
    }

    prepareSt2() {
        const titles = this.DOM.titlesSt2.filter((title) => {
            if (this.$header && this.$header.contains(title)) return false;
            if (title.closest('[data-reveal-item]')) return false;
            return true;
        });

        this._titleObserver.observe(titles);
    }

    destroy() {
        this._blockObserver.disconnect();
        this._itemObserver.disconnect();
        this._titleObserver.disconnect();
    }
}
